import type {
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  ColumnVisibilityState,
  RowData,
  SortingState,
} from "@tanstack/react-table-v9";

import { ACTIONS_COLUMN_ID, pinActionsColumn } from "./columns";
import { getColumnLabel, isUtilityColumn } from "./columnUtils";
import type { TableV2View } from "./hooks/usePivotView";
import type { TableV2Instance } from "./hooks/useTableV2";
import type { PivotConfig } from "./pivot/types";

export interface TableV2Layout {
  columnOrder: ColumnOrderState;
  columnVisibility: ColumnVisibilityState;
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  sorting: SortingState;
  pageSize: number;
  view?: TableV2View;
  pivot?: PivotConfig;
}

const MAX_LISTED_COLUMNS = 8;

export const snapshotLayout = <TData extends RowData>(
  table: TableV2Instance<TData>,
): TableV2Layout => {
  const { state } = table;
  const pivot = table.v2?.pivot;
  return {
    columnOrder: state.columnOrder,
    columnVisibility: state.columnVisibility,
    columnPinning: state.columnPinning,
    columnSizing: state.columnSizing,
    sorting: state.sorting,
    pageSize: state.pagination.pageSize,
    ...(pivot && table.v2
      ? { view: table.v2.view, pivot: pivot.controller.config }
      : {}),
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isStrings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");
const isRecordOf = <T>(value: unknown, type: "boolean" | "number") =>
  isRecord(value) && Object.values(value).every((item) => typeof item === type)
    ? (value as Record<string, T>)
    : undefined;

/**
 * Keeps the fields of a saved `table_state` that have the expected shape. Saved views come from the API untyped
 * (the generated Filter type has no `table_state` until codegen runs after the API change merges)
 */
export const parseLayout = (value: unknown): Partial<TableV2Layout> | null => {
  if (!isRecord(value)) return null;
  const layout: Partial<TableV2Layout> = {};
  if (isStrings(value.columnOrder)) layout.columnOrder = value.columnOrder;
  const visibility = isRecordOf<boolean>(value.columnVisibility, "boolean");
  if (visibility) layout.columnVisibility = visibility;
  const sizing = isRecordOf<number>(value.columnSizing, "number");
  if (sizing) layout.columnSizing = sizing;
  const pinning = value.columnPinning;
  if (
    isRecord(pinning) &&
    (pinning.start === undefined || isStrings(pinning.start)) &&
    (pinning.end === undefined || isStrings(pinning.end))
  ) {
    layout.columnPinning = {
      start: (pinning.start as string[] | undefined) ?? [],
      end: (pinning.end as string[] | undefined) ?? [],
    };
  }
  if (
    Array.isArray(value.sorting) &&
    value.sorting.every(
      (sort) =>
        isRecord(sort) &&
        typeof sort.id === "string" &&
        typeof sort.desc === "boolean",
    )
  ) {
    layout.sorting = value.sorting as SortingState;
  }
  if (Number.isInteger(value.pageSize) && (value.pageSize as number) > 0) {
    layout.pageSize = value.pageSize as number;
  }
  if (value.view === "table" || value.view === "pivot")
    layout.view = value.view;
  if (
    isRecord(value.pivot) &&
    Array.isArray(value.pivot.rows) &&
    Array.isArray(value.pivot.columns) &&
    Array.isArray(value.pivot.values)
  ) {
    layout.pivot = value.pivot as unknown as PivotConfig;
  }
  return layout;
};

/**
 * Applies a saved layout on top of the table's defaults. Columns the view doesn't mention keep their default
 * visibility (so newer default-hidden columns stay hidden), and ids of columns that no longer exist are dropped
 */
export const applyLayout = <TData extends RowData>(
  table: TableV2Instance<TData>,
  layout: Partial<TableV2Layout>,
) => {
  const ids = new Set(table.getAllLeafColumns().map((column) => column.id));
  const known = (list: string[] = []) => list.filter((id) => ids.has(id));
  const knownEntries = <T>(record: Record<string, T>) =>
    Object.fromEntries(Object.entries(record).filter(([id]) => ids.has(id)));

  if (layout.columnOrder) table.setColumnOrder(known(layout.columnOrder));
  if (layout.columnVisibility) {
    table.setColumnVisibility({
      ...table.defaultColumnVisibility,
      ...knownEntries(layout.columnVisibility),
    });
  }
  if (layout.columnPinning) {
    table.setColumnPinning(
      pinActionsColumn(
        {
          start: known(layout.columnPinning.start),
          end: known(layout.columnPinning.end),
        },
        ids.has(ACTIONS_COLUMN_ID),
      ),
    );
  }
  if (layout.columnSizing) {
    table.setColumnSizing(knownEntries(layout.columnSizing));
  }
  if (layout.sorting) {
    table.setSorting(layout.sorting.filter((sort) => ids.has(sort.id)));
  }
  if (layout.pageSize) {
    if (table.applyPageSize) table.applyPageSize(layout.pageSize);
    else table.setPagination({ pageIndex: 0, pageSize: layout.pageSize });
  }
  const pivot = table.v2?.pivot;
  if (pivot && layout.pivot) pivot.controller.setConfig(layout.pivot);
  if (pivot && layout.view) table.v2?.setView(layout.view);
};

// Plain-language lines for what applying the layout does, e.g. "Sorted by Name (ascending)"
export const describeLayout = <TData extends RowData>(
  table: TableV2Instance<TData>,
  layout: Partial<TableV2Layout>,
): string[] => {
  const all = table
    .getAllLeafColumns()
    .filter(
      (column) => !isUtilityColumn(column) && column.id !== ACTIONS_COLUMN_ID,
    );
  const label = (id: string) => {
    const column = all.find((c) => c.id === id);
    return column ? getColumnLabel(column) : undefined;
  };
  const order = layout.columnOrder ?? [];
  const rank = (id: string) => {
    const index = order.indexOf(id);
    return index === -1
      ? order.length + all.findIndex((c) => c.id === id)
      : index;
  };
  const start = layout.columnPinning?.start ?? [];
  const end = layout.columnPinning?.end ?? [];
  const region = (id: string) =>
    start.includes(id) ? 0 : end.includes(id) ? 2 : 1;
  const shown = all
    .filter((column) => layout.columnVisibility?.[column.id] !== false)
    .sort((a, b) => region(a.id) - region(b.id) || rank(a.id) - rank(b.id))
    .map(getColumnLabel);

  const lines: string[] = [];
  const listed = shown.slice(0, MAX_LISTED_COLUMNS).join(", ");
  const more = shown.length - MAX_LISTED_COLUMNS;
  lines.push(
    `Shows ${shown.length} of ${all.length} columns: ${listed}${more > 0 ? `, and ${more} more` : ""}`,
  );
  const pinned = start.map(label).filter(Boolean);
  if (pinned.length) lines.push(`Pinned left: ${pinned.join(", ")}`);
  const pinnedRight = end.map(label).filter(Boolean);
  if (pinnedRight.length) lines.push(`Pinned right: ${pinnedRight.join(", ")}`);
  const sorted = (layout.sorting ?? [])
    .map((sort) => {
      const name = label(sort.id);
      return name && `${name} (${sort.desc ? "descending" : "ascending"})`;
    })
    .filter(Boolean);
  if (sorted.length) lines.push(`Sorted by ${sorted.join(", then ")}`);
  const resized = Object.keys(layout.columnSizing ?? {}).filter(label).length;
  if (resized) {
    lines.push(
      `Custom width on ${resized} ${resized === 1 ? "column" : "columns"}`,
    );
  }
  if (layout.pageSize) lines.push(`${layout.pageSize} rows per page`);
  if (layout.view === "pivot") lines.push("Opens as a pivot");
  return lines;
};
